import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { computed } from 'mobx';
import { CircularProgress } from '@mui/material';
import format from 'string-template';

import useSessionStore from '../../../../../hooks/useSessionStore';
import useLogger from '../../../../../hooks/useLogger';
import useToast from '../../../../hooks/useToast';
import LoadingPage from '../../../../../components/LoadingPage';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import FeedContext from '../../../../context/FeedContext';
import FeedState from '../../../../context/FeedContext/states';
import FeedType from '../../../../context/FeedContext/config/feedTypes';
import FeedActivity from '../FeedActivity';
import FeedEmptyView from '../FeedEmptyView';
import WorkoutConflictReviewFeed from '../WorkoutConflictReviewFeed';
import ConsolidatedToDoFeed from '../ConsolidatedToDoFeed';
import QAPContext from '../../../../../context/QAPContext';
import { usePodFilter } from '../../../../../context/PodContext';
import clientInfoTabs from '../../../../components/ClientInfo/tabs';
import TrackAssignmentModal from '../TrackAssignmentModal';
import { sortFeedItems } from './utils/sortFeedItems';
import {
  FeedContainer,
  StyledLoaderContainer,
  ClearAllContainer,
} from './styles';
import texts from './texts.json';

const DEFAULT_ACTIVITIES_PER_PAGE = 10;
const CHECK_IN_ACTIVITIES_PER_PAGE = 4;

const Feed = () => {
  const {
    authUser: {
      uid: authUserId,
    } = {},
    isCoachAssistant,
  } = useSessionStore();
  const {
    selectedFeedType,
    feedActivityList,
    archivedActivityList,
    feedState,
    claimedChannelMap,
    onMarkAllAsRead,
  } = useContext(FeedContext);

  const { showToast } = useToast();

  const listRef = useRef();
  const [page, setPage] = useState(1);
  const [listItems, setListItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [showClearAllConfirmation, setShowClearAllConfirmation] = useState(false);
  const [trackAssignmentModalState, setTrackAssignmentModalState] = useState({
    show: false,
    userDoc: null,
    productId: undefined,
  });

  const { openQAP } = useContext(QAPContext);
  const { logEvent } = useLogger();
  /*
   * A selected pod already scopes the feed to that pod's clients, so the claim/interaction
   * bucket sort is skipped in favour of the AC's configured sort direction. See S2-8290.
   */
  const { shouldFilter: isPodSelected } = usePodFilter();
  const hasLoggedFeedViewRef = useRef(null);

  /*
    we need to handle action click here because most of the time when we complete an action,
    feed item will be removed from the list and what ever we do in FeedActivity will be unmounted
  */
  const onActivityActionClick = useCallback(({
    feedType, userId, productId, clientUserDoc,
  }) => {
    if (feedType === FeedType.NEED_NEW_WORKOUTS) {
      // The card names the product that is short, so the assign modal opens on it.
      openQAP(userId, clientInfoTabs.WORKOUT_CALENDAR, productId);
    } else if (feedType === FeedType.ASSIGN_TRACK) {
      // Without the real user model the selector can't offer the client's products.
      if (!clientUserDoc) {
        return;
      }
      setTrackAssignmentModalState({
        show: true,
        userDoc: clientUserDoc,
        productId,
      });
    }
  }, [openQAP]);

  // This is used to identify if a chat is currently claimed by the current user or someone else.
  const getChannelClaim = useCallback((clientId) => {
    const channel = claimedChannelMap.get(clientId);

    const isChannelSelfClaimed = !!channel && channel.claimedBy === authUserId;
    const isChannelClaimed = !!channel && channel.claimedBy !== authUserId;

    return { isChannelSelfClaimed, isChannelClaimed, claimedChannel: channel };
  }, [
    claimedChannelMap,
    authUserId,
  ]);

  const currentFeedList = useMemo(() => computed(() => {
    const feedList = selectedFeedType === FeedType.ARCHIVED ? archivedActivityList : feedActivityList;
    const filteredFeedList = feedList.reduce((arr, current) => {
      if (current.type !== selectedFeedType) {
        return arr;
      }
      const found = arr.find((activity) => current.workoutAssignmentId
        && activity.workoutAssignmentId === current.workoutAssignmentId);

      const curr = {
        ...current.data,
        id: current.id,
      };
      if (!found) {
        curr.id = [curr.id];
        arr.push(curr);
      } else {
        found.content = `${curr.content}\n\n${found.content}`;
        found.id = [...found.id, curr.id];
        if (curr.workoutVideoAvailable) {
          found.workoutVideoAvailable = curr.workoutVideoAvailable;
          found.user = curr.user;
          found.gameplaySessionId = curr.gameplaySessionId;
        }
      }
      return arr;
    }, []);

    // If the user is an AC, sort the feed items accordingly
    if (isCoachAssistant) {
      return sortFeedItems(filteredFeedList, { authUserId, getChannelClaim, isPodSelected });
    }
    // If not, return the feed list as it is
    return filteredFeedList;
  }), [
    feedActivityList,
    selectedFeedType,
    archivedActivityList,
    isCoachAssistant,
    authUserId,
    getChannelClaim,
    isPodSelected,
  ]).get();

  useEffect(() => {
    setListItems([]);
    setPage(1);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [
    selectedFeedType,
  ]);

  useEffect(() => {
    if (selectedFeedType === FeedType.DISENGAGED_CLIENT && hasLoggedFeedViewRef.current !== selectedFeedType) {
      hasLoggedFeedViewRef.current = selectedFeedType;
      logEvent('disengaged_feed_viewed', {
        ac_id: authUserId,
        client_count_displayed: currentFeedList.length,
      });
    } else if (selectedFeedType !== FeedType.DISENGAGED_CLIENT) {
      hasLoggedFeedViewRef.current = null;
    }
  }, [selectedFeedType, currentFeedList.length, logEvent, authUserId]);

  useEffect(() => {
    const activitiesPerPage = (selectedFeedType === FeedType.CHECK_IN)
      ? CHECK_IN_ACTIVITIES_PER_PAGE
      : DEFAULT_ACTIVITIES_PER_PAGE;
    const endIndex = activitiesPerPage * page;
    const items = currentFeedList.slice(0, endIndex);
    setListItems(items);
  }, [
    currentFeedList,
    page,
    selectedFeedType,
  ]);

  const handleScroll = useCallback((e) => {
    const element = e.target;
    // check if user has scrolled to the bottom of the current page
    // we subtract 30px to load the next page before user reaches the bottom for smoother UX
    if (Math.floor(element.scrollHeight - element.scrollTop) - 30 < element.clientHeight) {
      // if we have already loaded all the activities, do nothing
      if (currentFeedList.length === listItems.length) {
        return;
      }
      setIsLoading(true);
      const nextPage = page + 1;
      setPage(nextPage);
      setIsLoading(false);
    }
  }, [
    page,
    currentFeedList,
    listItems,
  ]);

  const handleClearAll = useCallback(async () => {
    setShowClearAllConfirmation(false);
    setIsClearingAll(true);
    // A failed archive surfaces through the FeedContainer actionError snackbar,
    // same as the single Mark as Read path — no second notification here.
    const response = await onMarkAllAsRead();
    if (response) {
      showToast(texts.clearAllSuccess);
    }
    setIsClearingAll(false);
  }, [onMarkAllAsRead, showToast]);

  if (feedState !== FeedState.FEED_INITIALIZED) {
    return <LoadingPage />;
  }

  if (selectedFeedType === FeedType.WORKOUT_CONFLICT_REVIEW) {
    return (
      <FeedContainer>
        <WorkoutConflictReviewFeed />
      </FeedContainer>
    );
  }

  if (selectedFeedType === FeedType.TODO) {
    return (
      <FeedContainer>
        <ConsolidatedToDoFeed />
      </FeedContainer>
    );
  }

  if (currentFeedList.length === 0) {
    return (
      <FeedContainer>
        <FeedEmptyView
          activityType={selectedFeedType}
        />
      </FeedContainer>
    );
  }

  return (
    <FeedContainer onScroll={handleScroll} ref={listRef}>
      {selectedFeedType === FeedType.DISENGAGED_CLIENT && (
        <ClearAllContainer>
          <PrimaryButton
            onClick={() => setShowClearAllConfirmation(true)}
            variant="dark"
            size="medium"
            disabled={isClearingAll}
          >
            {texts.clearAll}
          </PrimaryButton>
        </ClearAllContainer>
      )}
      {isClearingAll && <StyledLoaderContainer><CircularProgress /></StyledLoaderContainer>}
      {listItems.map((activity) => (
        <div key={activity.id}>
          <FeedActivity
            activity={activity}
            userId={activity.user}
            onActivityActionClick={onActivityActionClick}
          />
        </div>
      ))}
      {isLoading && <StyledLoaderContainer><CircularProgress /></StyledLoaderContainer>}
      <TrackAssignmentModal
        showModal={trackAssignmentModalState.show}
        userDoc={trackAssignmentModalState.userDoc}
        productId={trackAssignmentModalState.productId}
        onClose={() => setTrackAssignmentModalState({ show: false, userDoc: null, productId: undefined })}
      />
      <ConfirmDialog
        isOpen={showClearAllConfirmation}
        onCancel={() => setShowClearAllConfirmation(false)}
        onConfirm={handleClearAll}
        confirmDisabled={isClearingAll}
        dialogTexts={{
          title: texts.clearAllConfirmationTitle,
          content: format(texts.clearAllConfirmationContent, { count: currentFeedList.length }),
        }}
      />
    </FeedContainer>
  );
};

export default compose(
  observer,
)(Feed);
